import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { Checkbox, HighLightText, ModalDialog, Typography } from "src/blitz"
import translate, {
  TranslateComponent,
  useBulkTanslate
} from "src/common/translations"
import { setSelectedAHUProperties } from "src/redux/slicers/reportSlice"
import { selectLanguage } from "src/redux/slicers/userData"
import { SearchRegex } from "src/utils/CommonMethods"

import { BUTTON } from "../../Charts/constant"
import styles from "../ChillerReport/ChillerPerformanceReport.module.scss"
import propertiesListStyles from "./AHUPropertiesList.module.scss"
import { getAHUProperties } from "./helper"

type ModalConfig = {
  name: string
  info: {
    preSectionNameText?: string
    description: string
    imageSrc?: string
    dialogWidth?: string
    imageWidth?: string
    imageHeight?: string
  }
}

const AHUPropertiesList = ({
  selectedProperties = [],
  searchText = "",
  handleChangeProperties = () => null,
  isDXCoolType = true
}: {
  selectedProperties?: string[]
  searchText?: string
  handleChangeProperties?: (value: string) => void
  isDXCoolType?: boolean
}) => {
  const dispatch = useDispatch()
  const [showModal, setShowModal] = useState<ModalConfig | null>(null)
  const { finalProps, defaultSelectedProperties } = useMemo(
    () => getAHUProperties(isDXCoolType),
    [isDXCoolType]
  )
  const [translatedProperty, setTranslatedProperty] = useState<
    Record<string, string>
  >({})

  const selectedLanguage = useSelector(selectLanguage) || "en"
  const bulkTranslate = useBulkTanslate()

  useEffect(() => {
    dispatch(setSelectedAHUProperties(defaultSelectedProperties))
  }, [dispatch, defaultSelectedProperties])

  useEffect(() => {
    let isMounted = true

    const translateProperties = async () => {
      if (!finalProps?.length || selectedLanguage === "en") {
        if (isMounted) setTranslatedProperty({})
        return
      }

      const newTranslatedNames = await bulkTranslate(
        finalProps.reduce<Record<string, string>>((acc, prop) => {
          acc[String(prop.name)] = prop.name
          return acc
        }, {})
      )

      if (!isMounted) return
      setTranslatedProperty(newTranslatedNames)
    }

    translateProperties()

    return () => {
      isMounted = false
    }
  }, [selectedLanguage, bulkTranslate, finalProps])

  const filteredProps = useMemo(() => {
    const translatedProps = finalProps.map((prop) => ({
      ...prop,
      translatedPropertyName: translatedProperty[String(prop.name)] || prop.name
    }))
    if (searchText) {
      const newProps = translatedProps.filter((property) =>
        SearchRegex(property?.translatedPropertyName, searchText)
      )
      return newProps
    }
    return translatedProps
  }, [translatedProperty, searchText, finalProps])

  const { modalConfig, customStyles } = useMemo(() => {
    const modalDialog = {
      heading: showModal?.name ?? "",
      modal: Boolean(showModal),
      handleClose: () => setShowModal(null),
      buttons: [
        {
          text: "Close",
          type: BUTTON,
          variant: "blue",
          handleClick: () => {
            setShowModal(null)
          }
        }
      ]
    }
    const customStyles = showModal?.info?.dialogWidth && {
      maxWidth: showModal?.info?.dialogWidth
    }

    return {
      modalConfig: modalDialog,
      customStyles
    }
  }, [showModal])

  const theTranslate = translate("The")
  const translateTooltip = translate(
    "View section description and example image"
  )
  return (
    <>
      {filteredProps?.map((properties, index: number) => {
        const checkboxId = `${properties.value}-${index}`
        return (
          <div key={properties.value}>
            <div className={styles.propertiesLabel}>
              <Checkbox
                key={checkboxId}
                name={properties.value}
                label={
                  <HighLightText
                    name={properties?.translatedPropertyName}
                    searchText={searchText}
                    translateLabel={false}
                  />
                }
                onChange={() => {
                  handleChangeProperties(properties.value)
                }}
                checked={selectedProperties?.includes(properties.value)}
              />
              <button
                type="button"
                className={propertiesListStyles.infoBlock}
                title={translateTooltip}
                onClick={() => {
                  setShowModal({
                    name: properties?.name,
                    info: properties?.info
                  })
                }}
              >
                <span className="icon icon-info" />
              </button>
            </div>
          </div>
        )
      })}
      <ModalDialog
        modalConfig={modalConfig}
        customStyles={customStyles}
        bodyClassName={styles?.infoImageBody}
        footerClassName={styles?.infoFooter}
      >
        <div>
          <Typography className={styles?.description} tagType="h4">
            <b>
              <TranslateComponent>Description:</TranslateComponent>
            </b>
          </Typography>
          <Typography tagType="div" styleType="p4">
            <>
              {showModal?.info?.preSectionNameText ? (
                <>
                  <TranslateComponent>
                    {showModal?.info?.preSectionNameText}
                  </TranslateComponent>{" "}
                </>
              ) : (
                <>{theTranslate} </>
              )}
              {
                <b>
                  {showModal?.name ? (
                    <TranslateComponent>{showModal?.name}</TranslateComponent>
                  ) : (
                    ""
                  )}
                </b>
              }{" "}
              <TranslateComponent>
                {showModal?.info?.description}
              </TranslateComponent>
            </>
          </Typography>
        </div>
        {showModal?.info?.imageSrc && (
          <div>
            <Typography className={styles?.description} tagType="h4">
              <b>
                <TranslateComponent>Section Example:</TranslateComponent>
              </b>
            </Typography>
            <div className={styles?.imageWrapperBorder}>
              <div
                style={{
                  maxWidth: showModal?.info?.imageWidth,
                  maxHeight: showModal?.info?.imageHeight
                }}
                className={styles?.imageWrapper}
              >
                <img src={showModal?.info?.imageSrc} alt={showModal?.name} />
              </div>
            </div>
          </div>
        )}
      </ModalDialog>
    </>
  )
}

export default AHUPropertiesList

